/**
 * Image Collection Carousel
 * Vanilla JS coverflow-style slider. No external libraries.
 * Center slide is full size; neighbors peek in on either side; infinite loop
 * via shortest-path circular positioning (no DOM cloning required).
 */
(function () {
  'use strict';

  var instances = new WeakMap();

  var SCALE_ACTIVE = 1.06;
  var SCALE_INACTIVE = 0.88;
  var GAP_PX = 24;
  var SWIPE_THRESHOLD_PX = 50;
  var DRAG_CLICK_SUPPRESS_PX = 8;

  function circularDiff(index, activeIndex, total) {
    var diff = index - activeIndex;
    diff = ((diff % total) + total) % total;
    if (diff > total / 2) diff -= total;
    return diff;
  }

  function createState(root) {
    var carousel = root.querySelector('[data-icc-carousel]');
    var track = root.querySelector('[data-icc-track]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-icc-slide]'));
    var prevBtn = root.querySelector('[data-icc-prev]');
    var nextBtn = root.querySelector('[data-icc-next]');
    var scrollBtn = root.querySelector('[data-icc-scroll-down]');

    var state = {
      root: root,
      carousel: carousel,
      track: track,
      slides: slides,
      prevBtn: prevBtn,
      nextBtn: nextBtn,
      scrollBtn: scrollBtn,
      activeIndex: 0,
      total: slides.length,
      dragging: false,
      dragMoved: false,
      startX: 0,
      currentX: 0,
      autoplayTimer: null,
      resizeTimer: null
    };

    return state;
  }

  function measureStep(state) {
    if (!state.slides.length || !state.carousel) return 0;
    var slideRect = state.slides[0].getBoundingClientRect();
    return slideRect.width + GAP_PX;
  }

  function render(state) {
    if (!state.total) return;
    var step = measureStep(state);

    state.slides.forEach(function (slide, i) {
      var diff = circularDiff(i, state.activeIndex, state.total);
      var abs = Math.abs(diff);
      var isActive = diff === 0;
      var visible = abs <= 1;
      var translateX = diff * step;
      var scale = isActive ? SCALE_ACTIVE : SCALE_INACTIVE;

      slide.style.transform =
        'translate(-50%, 0) translateX(' + translateX + 'px) scale(' + scale + ')';
      slide.style.opacity = visible ? '1' : '0';
      slide.style.zIndex = String(10 - abs);
      slide.style.pointerEvents = visible ? 'auto' : 'none';
      slide.setAttribute('data-active', isActive ? 'true' : 'false');
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      slide.tabIndex = isActive ? 0 : -1;
    });
  }

  function goTo(state, index) {
    if (!state.total) return;
    state.activeIndex = ((index % state.total) + state.total) % state.total;
    render(state);
  }

  function next(state) {
    goTo(state, state.activeIndex + 1);
  }

  function prev(state) {
    goTo(state, state.activeIndex - 1);
  }

  function startAutoplay(state) {
    stopAutoplay(state);
    var enabled = state.root.getAttribute('data-autoplay') === 'true';
    if (!enabled || state.total <= 1) return;
    var speed = parseFloat(state.root.getAttribute('data-autoplay-speed')) || 5;
    state.autoplayTimer = window.setInterval(function () {
      next(state);
    }, speed * 1000);
  }

  function stopAutoplay(state) {
    if (state.autoplayTimer) {
      window.clearInterval(state.autoplayTimer);
      state.autoplayTimer = null;
    }
  }

  function bindPointerEvents(state) {
    var track = state.track;

    function onPointerDown(e) {
      state.dragging = true;
      state.dragMoved = false;
      state.startX = e.clientX;
      state.currentX = e.clientX;
      stopAutoplay(state);
    }

    function onPointerMove(e) {
      if (!state.dragging) return;
      state.currentX = e.clientX;
      if (Math.abs(state.currentX - state.startX) > DRAG_CLICK_SUPPRESS_PX) {
        state.dragMoved = true;
      }
    }

    function onPointerUp() {
      if (!state.dragging) return;
      var delta = state.currentX - state.startX;
      state.dragging = false;
      if (Math.abs(delta) > SWIPE_THRESHOLD_PX) {
        if (delta < 0) {
          next(state);
        } else {
          prev(state);
        }
      }
      startAutoplay(state);
    }

    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', onPointerUp);
    track.addEventListener('pointercancel', onPointerUp);
    track.addEventListener('pointerleave', function () {
      if (state.dragging) onPointerUp();
    });

    // Prevent click-through navigation when the gesture was a drag/swipe, and
    // bring a partially-visible neighbor to center instead of following its
    // link immediately, matching typical coverflow-carousel behavior.
    state.slides.forEach(function (slide) {
      slide.addEventListener('click', function (e) {
        if (state.dragMoved) {
          e.preventDefault();
          return;
        }
        var idx = parseInt(slide.getAttribute('data-index'), 10);
        if (idx !== state.activeIndex) {
          e.preventDefault();
          goTo(state, idx);
        }
      });
    });
  }

  function bindControls(state) {
    if (state.prevBtn) {
      state.prevBtn.addEventListener('click', function () {
        prev(state);
        stopAutoplay(state);
        startAutoplay(state);
      });
    }
    if (state.nextBtn) {
      state.nextBtn.addEventListener('click', function () {
        next(state);
        stopAutoplay(state);
        startAutoplay(state);
      });
    }
    if (state.scrollBtn) {
      state.scrollBtn.addEventListener('click', function () {
        var section = state.root.closest('.icc-section');
        var nextEl = section ? section.nextElementSibling : null;
        if (nextEl && nextEl.scrollIntoView) {
          nextEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }

    state.carousel.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        next(state);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev(state);
      }
    });

    state.carousel.addEventListener('mouseenter', function () {
      stopAutoplay(state);
    });
    state.carousel.addEventListener('mouseleave', function () {
      startAutoplay(state);
    });
  }

  function bindResize(state) {
    window.addEventListener('resize', function () {
      window.clearTimeout(state.resizeTimer);
      state.resizeTimer = window.setTimeout(function () {
        render(state);
      }, 100);
    });
  }

  function init(root) {
    if (!root || instances.has(root)) {
      if (root && instances.has(root)) render(instances.get(root));
      return;
    }

    var state = createState(root);
    if (!state.total) return;

    instances.set(root, state);

    bindPointerEvents(state);
    bindControls(state);
    bindResize(state);

    // Ensure layout is settled before first measurement.
    window.requestAnimationFrame(function () {
      render(state);
      startAutoplay(state);
    });
  }

  function goToPublic(root, index) {
    var state = instances.get(root);
    if (state) {
      stopAutoplay(state);
      goTo(state, index);
    }
  }

  window.ImageCollectionCarousel = {
    init: init,
    goTo: goToPublic
  };

  // Auto-init any carousels already in the DOM on script load.
  document.querySelectorAll('[data-icc-section]').forEach(function (root) {
    init(root);
  });
})();
